<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('unitStatisticsPage.pageTitle') }}
    </v-toolbar>

    <v-sheet color="grey lighten-3"
             class="px-4 pt-4 pb-3"
             tile>
      <h3 class="transition-swing font-weight-medium">{{ $t('statisticsTable.filterTitle') }}</h3>
      <v-responsive>
        <v-row>
          <v-col cols="12" sm="6" md="2" class="py-2">
            <DateInput v-model="filters.start"
                       :label="$t('statisticsTable.labels.startDate')"
                       :dense="true"
                       @change="load"
                       min-width="0"
                       max-width="80%"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" class="py-2">
            <DateInput v-model="filters.end"
                       :label="$t('statisticsTable.labels.endDate')"
                       :dense="true"
                       @change="load"
                       min-width="0"
                       max-width="80%"
            />
          </v-col>
          <v-col cols="12" sm="6" md="8" class="py-2 mt-auto mb-auto d-flex justify-end">
            <v-btn large color="primary" @click="load">
              {{ $t('statisticsTable.actions.update') }}
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn style="margin-left: 8px" large color="warning" class="me-2" dark @click="onExcelButtonClicked">{{ $t('statisticsTable.actions.toExcel') }}</v-btn>
            <v-btn large color="primary" @click="onPrintClicked">
              {{ $t('statisticsTable.actions.print') }}
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-responsive>
    </v-sheet>

    <v-card-text>
      <v-simple-table :dense="true">
        <template v-slot:default>
          <thead>
          <tr>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.number') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('common.labels.department') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.acceptedForPeriod') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.over3Sheets') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.pageCount') }}</th>
            <th colspan="2" class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.price') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForRegular') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForOver3Sheets') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.totalSum') }}
            </th>
          </tr>
          <tr>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.regularMail') }}
            </th>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.over3Sheets') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <template v-if="stats.length">
            <tr v-for="(item, index) in stats" :key="index">
              <td class="text-center">{{ index + 1 }}</td>

              <td>
                <template v-if="item.unit">
                  {{ item.unit.name }}
                </template>
              </td>

              <td class="text-center">{{ item.arrivedCount | decimalNoTrim }}</td>

              <td class="text-center">{{ item.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center">{{ item.pagesCount | decimalNoTrim }}</td>

              <td class="text-center">{{ item.priceSimple | decimalNoTrim }}</td>
              <td class="text-center">{{ item.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-center">{{ item.costSimple | decimalNoTrim }}</td>
              <td class="text-center">{{ item.costOverPage | decimalNoTrim }}</td>
              <td class="text-center">{{ item.totalCost | decimalNoTrim }}</td>
            </tr>

            <tr>
              <td class="text-center font-weight-black" colspan="2">{{ $t('statisticsTable.footer.total') }}</td>
              <td class="text-center font-weight-black">{{ totalStats.arrivedCount | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.pagesCount | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.priceSimple | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.costSimple | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.costOverPage | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.totalCost | decimalNoTrim }}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="11" class="text-center">{{ $t('statisticsTable.noData') }}</td>
            </tr>
          </template>

          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP, LAYOUT_TITLE_CHANGED} from "@/store/modules/layout/actions";
import OrganizationService from "@/services/organization.service";
import StatisticsService from "@/services/statistics.service";
import _ from "underscore";
import Mappers from "@/mappers";
import DateInput from "@/components/input/DateInput";
import UnitService from "@/services/unit.service";
import XLSX from "xlsx";


export default {
  name: "UserUnitStatisticsPage",
  components: {
    DateInput
  },
  data() {
    return {
      organizations: [],
      units: [],
      stats: [],
      totalStats: {
        arrivedCount: 0,
        overPagesSum: 0,
        pagesCount: 0,
        priceSimple: '',
        pricePerOverPage: '',
        costSimple: 0,
        costOverPage: 0,
        totalCost: 0
      },
      filters: {
        inn: this.$store.getters.inn,
        unit: null,
        start: null,
        end: null,
        isExcel: false
      }
    }
  },
  methods: {
    onExcelButtonClicked(){
      this.filters.isExcel = true;
      this.load()
    },
    formatNumberWithSpaces(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    load() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      this.totalStats.arrivedCount = 0
      this.totalStats.overPagesSum = 0
      this.totalStats.pagesCount = 0

      this.totalStats.costSimple = 0
      this.totalStats.costOverPage = 0
      this.totalStats.totalCost = 0

      this.loadUnits();

      StatisticsService.getUnitStatisticsByUser({
        inn: this.filters.inn,
        start: this.filters.start,
        end: this.filters.end
      })
          .then(response => {
            this.stats = _.map(response.data, s => {
              return {
                unit: _.find(this.units, u => { return u.id === s.Unit.Id }),
                arrivedCount: s.Count,
                overPagesSum: s.OverPagedCount,
                pagesCount: s.PagesCount,
                priceSimple: s.PriceSimple,
                pricePerOverPage: s.PriceForOverPaged,
                costSimple: s.Count * s.PriceSimple,
                costOverPage: s.OverPagedCount * s.PriceForOverPaged,
                totalCost: (s.OverPagedCount * s.PriceForOverPaged) + (s.Count * s.PriceSimple)
              }
            })

            _.each(this.stats, s => {
              this.totalStats.arrivedCount += s.arrivedCount
              this.totalStats.overPagesSum += s.overPagesSum
              this.totalStats.pagesCount += s.pagesCount

              this.totalStats.costSimple += s.costSimple
              this.totalStats.costOverPage += s.costOverPage
              this.totalStats.totalCost += s.totalCost
            })

            if (this.filters.isExcel) {
              const workbook = XLSX.utils.book_new();
              const worksheet = XLSX.utils.aoa_to_sheet([
                [
                  this.$t('statisticsTable.headers.number'),
                  this.$t('common.labels.department'),
                  this.$t('statisticsTable.headers.acceptedForPeriod'),
                  this.$t('statisticsTable.headers.over3Sheets'),
                  this.$t('statisticsTable.headers.pageCount'),
                  this.$t('statisticsTable.headers.regularMail'),
                  this.$t('statisticsTable.headers.over3Sheets'),
                  this.$t('statisticsTable.headers.sumForRegular'),
                  this.$t('statisticsTable.headers.sumForOver3Sheets'),
                  this.$t('statisticsTable.headers.totalSum')
                ],
              ]);

              worksheet['!cols'] = [{ width: 10 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 25 }, { width: 20 }];
              worksheet['!rows'] = [{ hpx: 25 }];

              for (let i = 1; i <= 10; i++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i - 1 });
                const cell = worksheet[cellAddress];
                if (!cell) continue;

                cell.s = {
                  alignment: {
                    horizontal: 'center',
                    vertical: 'center',
                  },
                  fill: {
                    fgColor: { rgb: '4472C4' },
                  },
                  border: {
                    top: { style: 'thin', color: { rgb: '000000' } },
                    bottom: { style: 'thin', color: { rgb: '000000' } },
                    left: { style: 'thin', color: { rgb: '000000' } },
                    right: { style: 'thin', color: { rgb: '000000' } },
                  },
                };
              }

              this.stats.forEach((item, index) => {

                const row = [
                  index + 1,
                  item.unit ? item.unit.name : '',
                  this.formatNumberWithSpaces(item.arrivedCount),
                  this.formatNumberWithSpaces(item.overPagesSum),
                  this.formatNumberWithSpaces(item.pagesCount),
                  this.formatNumberWithSpaces(item.priceSimple),
                  this.formatNumberWithSpaces(item.pricePerOverPage),
                  this.formatNumberWithSpaces(item.costSimple),
                  this.formatNumberWithSpaces(item.costOverPage),
                  this.formatNumberWithSpaces(item.totalCost)
                ];

                XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: index + 1 });
              });

              XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

              const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
              const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'statistics.xlsx');
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadUnits() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      return UnitService.getUserUnits({inn: this.filters.inn})
          .then(response => {
            let displayNumber = 0
            this.units = _.map(response.data, u => {
              let unit = Mappers.mapUnit(u)
              unit.displayNumber = ++displayNumber

              return unit
            })
            this.total = this.units.length
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadOrganizations() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      return OrganizationService.getOrganizations({})
          .then(response => {
            this.organizations = _.filter(response.data.Records, o => {
              return o?.SentMailsCount > 0 && o?.Organization?.UnitsCount > 1
            })
            this.organizations = _.map(this.organizations, o => {
              return Mappers.mapOrganization(o?.Organization)
            })

            if (this.organizations.length)
              this.filters.inn = this.organizations[0].inn
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    onPrintClicked() {
      this.loading = true;
      this.$store.dispatch(LAYOUT_LOADING_START)

      StatisticsService.printUnitStatisticsByUser({
        inn: this.filters.inn,
        start: this.filters.start,
        end: this.filters.end,
        isPdf: true
      })
          .then(() => { })
          .catch(() => { })
          .finally(() => {
            this.loading = false;
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    }
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('unitStatisticsPage.pageTitle'))

    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn

    // this.loadOrganizations().then(()=> {
      this.loadUnits().then(() => {
        this.load()
      })
    // })
  }
}
</script>

<style scoped>
td, th {
  border: thin solid rgba(0, 0, 0, 0.12);
}

th {
//border: thin solid rgba(39, 22, 22, 68) !important;
}
</style>